import { db } from "./db";
import { hasSessionKey, decryptData } from "./crypto";
import { consolidate } from "@/lib/fiscal-consolidation";
import { useFiscalStore } from "@/store/fiscal-store";
import type { NotaSimplificada, NotaStatus } from "@/lib/fiscal-types";

/**
 * Descriptografa os itens completos das notas armazenadas no IndexedDB
 * e injeta no array `notas` do store. Deve ser chamada após syncLocalDBToStore.
 * 
 * Necessário para alimentar InsightsPage (produtos, cesta, pagamentos, churn).
 */
export async function hydrateNotasWithItems(perfilId?: string): Promise<void> {
  if (!hasSessionKey()) return;

  try {
    const store = useFiscalStore.getState();
    const userId = store.userId;
    if (!userId) return;

    const updatedNotas = [...store.notas];
    const filtro = store.filtroPeriodo;
    const hasFiltro = filtro && filtro.inicio && filtro.fim;

    const query = db.notas.where("userId").equals(userId);

    let hydratedCount = 0;

    await query.each(async (row) => {
      if (!row.cipher || !row.iv) return;

      // Filtro de perfilId manual
      if (perfilId && row.perfilId !== perfilId) return;

      // Filtro de período precoce para evitar descriptografia desnecessária
      const dateOnly = (row.data || "").split("T")[0];
      if (hasFiltro && (dateOnly < filtro.inicio || dateOnly > filtro.fim)) return;

      try {
        const decrypted = await decryptData<NotaSimplificada>(row.cipher, row.iv);
        if (decrypted?.itens) {
          const idx = updatedNotas.findIndex(n => n.chave === row.chave);
          if (idx !== -1) {
            updatedNotas[idx] = {
              ...updatedNotas[idx],
              ...decrypted,
              // Garante que o itens e detalhes (que são pesados) foram realmente extraídos
              itens: decrypted.itens || [],
              detalhes: decrypted.detalhes || updatedNotas[idx].detalhes,
            };
            hydratedCount++;
          }
        }
      } catch {
        // Ignora falhas de descriptografia
      }
    });

    if (hydratedCount === 0) return;

    // RECALCULA O DASHBOARD com os dados hidratados (itens/detalhes)
    const filteredNotas = hasFiltro
      ? updatedNotas.filter(n => n.emissao.split("T")[0] >= filtro.inicio && n.emissao.split("T")[0] <= filtro.fim)
      : updatedNotas;

    // RECALCULA O DASHBOARD (agora com itens e filtro!)
    const updatedDashboard = consolidate(filteredNotas, []);

    store.set({ 
      notas: updatedNotas,
      filteredNotas: filteredNotas,
      dashboard: updatedDashboard
    });
    
    console.log(`[sync] Hidratação e Recalculo concluídos: ${hydratedCount} notas.`);
  } catch (err) {
    console.error("[sync] Erro na hidratação de itens:", err);
  }
}

/**
 * Hidrata apenas uma nota específica. Útil para o modal de detalhes.
 */
export async function hydrateSingleNota(chave: string): Promise<void> {
  try {
    const store = useFiscalStore.getState();
    const userId = store.userId;
    if (!userId) return;

    const row = await db.notas.where("[userId+chave]").equals([userId, chave]).first();
    if (!row) return;

    let decrypted: Partial<NotaSimplificada> | null = null;
    // Tenta descriptografar apenas se tivermos a chave e os dados cifrados
    if (hasSessionKey() && row.cipher && row.iv) {
      decrypted = await decryptData<NotaSimplificada>(row.cipher, row.iv);
    }

    // Fallback: Busca da tabela de itens (unencrypted) se não houver no cipher ou se ele estiver vazio
    let itemsFallback: any[] = [];
    if (!decrypted?.itens || decrypted.itens.length === 0) {
      console.log(`[sync] Usando fallback de itens (tabela itens) para ${chave}`);
      const rawItens = await db.itens.where("[userId+documentoChave]").equals([userId, chave]).toArray();
      itemsFallback = rawItens.map((it, i) => ({
        nItem: i + 1,
        cProd: it.cProd,
        xProd: it.xProd,
        ncm: it.ncm,
        cfop: it.cfop,
        uCom: it.uCom || "UN",
        qCom: it.qCom,
        vUnCom: it.vUnCom,
        vProd: it.vProd,
        vDesc: 0,
        vICMS: it.vICMS || 0,
        vIPI: it.vIPI || 0,
        vPIS: it.vPIS || 0,
        vCOFINS: it.vCOFINS || 0,
        impostos: {
          icms: { cst: it.cst || "00", vBC: it.vProd, vICMS: it.vICMS || 0 },
          icmsST: it.vICMSST ? { vBCST: 0, vICMSST: it.vICMSST } : undefined,
          fcp: it.vFCP ? { vFCP: it.vFCP } : undefined,
          ipi: it.vIPI ? { vBC: 0, vIPI: it.vIPI } : undefined,
          pis: it.vPIS ? { vBC: 0, vPIS: it.vPIS } : undefined,
          cofins: it.vCOFINS ? { vBC: 0, vCOFINS: it.vCOFINS } : undefined,
        }
      }));
    }

    const updatedNotas = [...store.notas];
    
    // Atualiza o store se houver mudanças (decrypted ou fallback)
    if (decrypted || itemsFallback.length > 0) {
      const idx = updatedNotas.findIndex(n => n.chave === chave);
      if (idx === -1) return;

      const baseNota = updatedNotas[idx];
      
      // Se não conseguimos descriptografar, montamos um "Mock Detalhes" para o DANFE não ficar em branco
      const mockDetalhes = !decrypted ? {
        emitente: { 
          xNome: row.emitente, 
          cnpjCpf: row.cnpjEmit, 
          IE: row.ieEmit,
          endereco: { 
            xLgr: row.enderEmit?.split(",")[0], 
            nro: row.enderEmit?.split(",")[1]?.trim(), 
            xBairro: row.bairroEmit,
            CEP: row.cepEmit,
            fone: row.foneEmit,
            xMun: row.municipioEmit, 
            UF: row.ufEmit 
          }
        },
        destinatario: { 
          xNome: row.destinatario, 
          cnpjCpf: row.cnpjDest, 
          IE: row.ieDest,
          endereco: { 
            xLgr: row.enderDest?.split(",")[0], 
            nro: row.enderDest?.split(",")[1]?.trim(), 
            xBairro: row.bairroDest,
            CEP: row.cepDest,
            fone: row.foneDest,
            xMun: row.municipioDest, 
            UF: row.ufDest 
          }
        },
        transporte: {
          modFrete: row.transpFreteMod,
          transporta: {
            xNome: row.transpNome,
            CNPJ: row.transpCnpj,
            IE: row.transpIE,
            xEnder: row.transpEnder,
            xMun: row.transpMun,
            UF: row.transpUF
          }
        },
        ide: { natOp: row.natOp, nNF: row.numero, serie: row.serie, dhSaiEnt: row.hSaiEnt ? `2000-01-01T${row.hSaiEnt}` : undefined },
        protocolo: { nProt: row.protocolo },
        infAdic: { infCpl: row.infCpl },
        total: { ICMSTot: { vTotTrib: row.vTotTrib } },
        emitente: { ...row.emitente as any, IEST: row.ieST }
      } : undefined;

      updatedNotas[idx] = {
        ...baseNota,
        ...decrypted,
        itens: decrypted?.itens || itemsFallback,
        detalhes: decrypted?.detalhes || mockDetalhes || baseNota.detalhes
      };

      store.set({ 
        notas: updatedNotas,
        filteredNotas: store.filteredNotas.map(n => n.chave === chave ? updatedNotas[idx] : n)
      });
      console.log(`[sync] Nota ${chave} hidratada. Itens encontrados: ${(decrypted?.itens || itemsFallback).length}`);
    } else {
      console.warn(`[sync] Nota ${chave} não encontrada no store para hidratação.`);
    }
  } catch (err) {
    console.error("[sync] Erro na hidratação individual:", err);
  }
}


/**
 * Sincroniza o banco local IndexedDB com o estado global da aplicação (Zustand).
 * 
 * @param overridePerfilId - Quando fornecido (pipeline pós-upload), usa este perfilId
 *   e só atualiza a lista de notas, preservando o dashboard calculado pelo worker.
 *   Quando omitido (desbloqueio/login), recalcula o dashboard completo.
 */
export async function syncLocalDBToStore(overridePerfilId?: string): Promise<void> {
  const store = useFiscalStore.getState();
  const userId = store.userId;
  
  if (!userId) {
    console.warn("[sync] Sincronização abortada: userId não definido no store.");
    return;
  }

  // Se não houver chave de sessão, não prosseguimos com o mapeamento (segurança)
  if (!hasSessionKey()) {
    console.warn("[sync] Sincronização parcial: chave de sessão ausente.");
  }

  try {
    let perfilId = overridePerfilId || store.perfilAtivo?.id;

    if (!perfilId) {
      const firstWithData = await db.notas?.where("userId").equals(userId).limit(1).first();
      if (firstWithData?.perfilId) {
        perfilId = firstWithData.perfilId;
      }
    }

    console.log(`[sync] Buscando dados para userId: ${userId}, perfilId: ${perfilId || 'Todos'}`);

    const notas: NotaSimplificada[] = [];
    
    // Processamento iterativo para economizar RAM em bases gigantes (80k+)
    // Evita o uso de toArray() que carregaria campos pesados (cipher/iv) desnecessariamente
    const query = db.notas.where("userId").equals(userId);

    const filtro = store.filtroPeriodo;
    const hasFiltro = filtro && filtro.inicio && filtro.fim;

    const totalCount = await query.count();
    let current = 0;

    // 1. Busca todos os itens do usuário de uma vez para mapeamento O(1) em memória
    // Isso é muito mais performático que fazer uma query por nota dentro do loop
    const allItens = await db.itens.where("userId").equals(userId).toArray();
    const itemsMap = new Map<string, any[]>();
    allItens.forEach(i => {
      if (!itemsMap.has(i.documentoChave)) itemsMap.set(i.documentoChave, []);
      itemsMap.get(i.documentoChave)?.push(i);
    });

    await query.each(row => {
      current++;
      if (current % 500 === 0 || current === totalCount) {
        store.set({ syncProgress: { current, total: totalCount } });
      }

      if (perfilId && row.perfilId !== perfilId) return;

      const emissaoDate = row.data || "";
      const dateOnly = emissaoDate.split("T")[0];

      if (hasFiltro) {
        if (dateOnly < filtro.inicio || dateOnly > filtro.fim) return;
      }

      // 2. Recupera os itens vinculados a esta nota
      const rawItens = itemsMap.get(row.chave) || [];
      const noteItens: NotaItem[] = rawItens.map(i => ({
        nItem: 0,
        cProd: i.cProd,
        xProd: i.xProd,
        ncm: i.ncm,
        cfop: i.cfop,
        uCom: i.uCom || "UN",
        qCom: i.qCom,
        vUnCom: i.vUnCom,
        vProd: i.vProd,
        vICMS: i.vICMS || 0,
        vIPI: i.vIPI || 0,
        vPIS: i.vPIS || 0,
        vCOFINS: i.vCOFINS || 0,
        impostos: {
          icms: { cst: i.cst || "" },
          fcp: { vFCP: i.vFCP || 0 },
          icmsST: { vICMSST: i.vICMSST || 0 }
        }
      }));

      notas.push({
        chave: row.chave,
        modelo: (row.modelo as 55 | 65) ?? 55,
        numero: row.numero || "",
        serie: row.serie || "",
        emissao: emissaoDate,
        emitente: row.emitente,
        cnpjEmit: row.cnpjEmit,
        ufEmit: row.ufEmit || "",
        destinatario: row.destinatario || "",
        cnpjDest: row.cnpjDest || "",
        ufDest: row.ufDest || "",
        natOp: row.natOp || "",
        infCpl: row.infCpl || "",
        valorOficial: row.valor,
        valorDeclarado: row.valor,
        valorItens: row.valor,
        qtdItens: noteItens.length,
        vProd: row.valor,
        vFrete: 0, vSeg: 0, vDesc: 0, vOutro: 0,
        vICMS: row.vICMS || 0,
        vST: row.vST || 0,
        vFCP: row.vFCP || 0,
        vFCPST: row.vFCPST || 0,
        vIPI: row.vIPI || 0,
        vPIS: row.vPIS || 0,
        vCOFINS: row.vCOFINS || 0,
        vIBS: row.vIBS || 0,
        vCBS: row.vCBS || 0,
        cfopPrincipal: row.cfopPrincipal || "",
        notaStatus: (row.notaStatus || "AUTORIZADA") as NotaStatus,
        tipoOperacao: (row.tipoOperacao || "INDEFINIDA") as any,
        itens: noteItens,
        impostosDetalhe: [
          { sigla: "ICMS",    valor: row.vICMS   || 0, base: 0, aliquota: 0, isentas: 0, outras: 0 },
          { sigla: "ICMS-ST", valor: row.vST     || 0, base: 0, aliquota: 0, isentas: 0, outras: 0 },
          { sigla: "FCP",     valor: row.vFCP    || 0, base: 0, aliquota: 0, isentas: 0, outras: 0 },
          { sigla: "FCP-ST",  valor: row.vFCPST  || 0, base: 0, aliquota: 0, isentas: 0, outras: 0 },
          { sigla: "IPI",     valor: row.vIPI    || 0, base: 0, aliquota: 0, isentas: 0, outras: 0 },
          { sigla: "PIS",     valor: row.vPIS    || 0, base: 0, aliquota: 0, isentas: 0, outras: 0 },
          { sigla: "COFINS",  valor: row.vCOFINS || 0, base: 0, aliquota: 0, isentas: 0, outras: 0 },
          { sigla: "IBS",     valor: row.vIBS    || 0, base: 0, aliquota: 0, isentas: 0, outras: 0 },
          { sigla: "CBS",     valor: row.vCBS    || 0, base: 0, aliquota: 0, isentas: 0, outras: 0 },
          { sigla: "ISSQN",   valor: row.vISSQN  || 0, base: 0, aliquota: 0, isentas: 0, outras: 0 },
        ],
        detalhes: { 
          ide: { tpNF: row.tipoOperacao === "SAIDA" ? 1 : 0 },
          transporte: row.transporte,
          cobranca: row.cobranca,
          pagamento: row.pagamento,
          infAdic: row.infAdic,
          emitente: { 
            xNome: row.emitente, 
            CNPJ: row.cnpjEmit, 
            IE: row.ieEmit,
            endereco: { xLgr: row.enderEmit, xBairro: row.bairroEmit, CEP: row.cepEmit, xMun: row.municipioEmit, UF: row.ufEmit, fone: row.foneEmit }
          },
          destinatario: { 
            xNome: row.destinatario, 
            CNPJ: row.cnpjDest, 
            IE: row.ieDest,
            endereco: { xLgr: row.enderDest, xBairro: row.bairroDest, CEP: row.cepDest, xMun: row.municipioDest, UF: row.ufDest, fone: row.foneDest }
          }
        } as any,
        divergencia: null,
        status: null,
        fonte: "LOCAL_DB" as any,
        loteId: row.loteId, 
      });
    });

    const filteredNotas = notas; // Já filtradas no loop .each
    const dashboard = consolidate(filteredNotas, store.divergencias || []);

    console.log(`[sync] Sincronização finalizada: ${notas.length} notas no total.`);

    store.set({
      notas,
      filteredNotas,
      dashboard,
      message: "Dados sincronizados com sucesso",
      lastSyncTime: new Date().toLocaleTimeString("pt-BR"),
      syncProgress: null
    });
  } catch (err) {
    useFiscalStore.getState().set({ syncProgress: null });
    console.error("[sync] Erro crítico na sincronização:", err);
  }
}
